import { cloneDeep, range, sortBy, groupBy } from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import styled from 'styled-components'
import { agentPostCalendarService } from '../../../core/services'
import * as constants from '../../../static/constants'
import * as interfaces from '../../../static/interfaces'
import CalendarGrid from './Grid'
import CalendarYearMonthSelect from './YearMonthSelect'

declare var window: interfaces.IWindow

interface IDay {
  date: moment.Moment
  blocked: boolean
  reserved: boolean
  price: number
  departures: any[]
}

interface IState {
  yearMonth: moment.Moment
  data: IDay[]
  fetching: boolean
}

interface IProps {
  post: any
  agent?: boolean
}

function getJsonData(selectedYearMonth: moment.Moment): IDay[] {
  const year = selectedYearMonth.year()
  const month = selectedYearMonth.month()
  const daysInMonth = selectedYearMonth.daysInMonth()

  return range(daysInMonth).map((count: number) => ({
    date: moment([year, month, count + 1]),
    blocked: false,
    reserved: false,
    departures: [],
  }))
}

const initialState = {
  yearMonth: moment([moment().year(), moment().month(), 1]),
  selectedDate: {
    start: null,
    end: null,
  },
  dragItem: null,
  data: getJsonData(moment()),
  edit: {
    reserved: false,
    availablility: 'available',
  },
  focusedInput: null,
  anyTouched: false,
  collections: [],
  fetching: false,
}

class Calendar extends React.Component<IProps, IState> {
  constructor(props) {
    super(props)
    this.state = cloneDeep(initialState)
  }

  public componentDidMount() {
    if (this.props.post.id === null) {
      return
    }
    this.getCalendarCollections(moment().format('YYYY-MM'))
  }

  public getCalendarCollections = async yearMonth => {
    this.setState({ fetching: true })

    const { startTimes, postCalendars, bokunAvailabilities } = await agentPostCalendarService.get(
      this.props.post.id,
      yearMonth
    )

    const groupedBokunAvailabilities = groupBy(bokunAvailabilities, 'date')
    const isPrivate = this.props.post.post_type === 'private'
    const formattedBokunAvailabilities = Object.keys(groupedBokunAvailabilities).map(key => {
      const availavilities = groupedBokunAvailabilities[key]
      return {
        date: moment(availavilities[0].localizedDate),
        blocked: false,
        reserved: false,
        price: 0,
        departures: availavilities.map(ba => {
          return {
            startTime: ba.startTime,
            maxPeople: ba.bookedParticipants + ba.availabilityCount,
            reservedPeople: ba.bookedParticipants,
            blocked: ba.unavailable,
            reserved: ba.soldOut,
            privateQuantity: isPrivate ? ba.bookedParticipants + ba.availabilityCount : 0,
            reservedCount: isPrivate ? ba.bookedParticipants : 0,
          }
        }),
      }
    })

    const formattedStartTimes = sortBy(
      startTimes.map(startTime => ({
        startDate: moment(startTime.start_date),
        endDate: moment(startTime.end_date),
        startTime: startTime.start_time,
        maxPeople: startTime.max_people,
        days: startTime.days,
      })),
      ({ startTime }) => constants.START_TIMES.findIndex(time => time === startTime)
    )

    const formattedPostCalendars = postCalendars.map(postCalendar => ({
      id: postCalendar.id,
      date: moment(postCalendar.day),
      maxPeople: postCalendar.max_people,
      reservedPeople: postCalendar.reserved_people,
      blocked: postCalendar.blocked,
      reserved: postCalendar.reserved,
      startTime: postCalendar.start_time,
      privateQuantity: this.props.post.private_quantity,
      reservedCount: postCalendar.reserved_count,
    }))

    const nextData = this.state.data.map(day => {
      let departures = []
      const dayIndex = day.date.day() + 1

      if (this.props.post.bokun_activity_id) {
        const ba = formattedBokunAvailabilities.find(
          item => item.date.format() === day.date.format()
        )
        if (ba) {
          departures = ba.departures
        }
      } else {
        formattedStartTimes.forEach(startTime => {
          if (
            day.date.isSameOrAfter(startTime.startDate) &&
            day.date.isSameOrBefore(startTime.endDate) &&
            startTime.days.includes(dayIndex)
          ) {
            departures.push({
              startTime: startTime.startTime,
              maxPeople: startTime.maxPeople,
              reservedPeople: 0,
              blocked: false,
              reserved: false,
              privateQuantity: this.props.post.private_quantity,
              reservedCount: 0,
            })
          }
        })

        formattedPostCalendars.forEach(postCalendar => {
          if (day.date.isSame(postCalendar.date)) {
            departures = departures.map(departure => {
              if (departure.startTime === postCalendar.startTime) {
                return {
                  ...departure,
                  postCalendarId: postCalendar.id,
                  maxPeople: postCalendar.maxPeople,
                  reservedPeople: postCalendar.reservedPeople,
                  blocked: postCalendar.blocked,
                  reserved: postCalendar.reserved,
                  privateQuantity: postCalendar.privateQuantity,
                  reservedCount: postCalendar.reservedCount,
                }
              } else {
                return departure
              }
            })
          }
        })
      }

      return {
        ...day,
        departures,
      }
    })

    this.setState({
      fetching: false,
      data: nextData,
    })
  }

  public changeYearMonth = (event: React.FormEvent<HTMLSelectElement | HTMLButtonElement>) => {
    const element = event.currentTarget as HTMLSelectElement | HTMLButtonElement
    const date = moment(element.value)

    this.setState({
      yearMonth: date,
      data: getJsonData(date),
    })
    this.getCalendarCollections(date.format('YYYY-MM'))
  }

  public handleToggleClose = async departure => {
    const params = {
      post_id: this.props.post.id,
      day: departure.date.format('YYYY-MM-DD'),
      start_time: departure.startTime,
      max_people: departure.maxPeople,
      blocked: !departure.blocked,
    }

    if (departure.postCalendarId) {
      const { flush } = await agentPostCalendarService.update(departure.postCalendarId, params)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } else {
      const { flush } = await agentPostCalendarService.create(params)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    }

    await this.getCalendarCollections(this.state.yearMonth.format('YYYY-MM'))
  }

  public handleUpdateDeparture = async departure => {
    const params = {
      post_id: this.props.post.id,
      day: departure.date.format('YYYY-MM-DD'),
      start_time: departure.startTime,
      max_people: departure.maxPeople,
      blocked: departure.blocked,
    }

    if (departure.postCalendarId) {
      const { flush } = await agentPostCalendarService.update(departure.postCalendarId, params)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } else {
      const { flush } = await agentPostCalendarService.create(params)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    }

    await this.getCalendarCollections(this.state.yearMonth.format('YYYY-MM'))
  }

  public render() {
    return (
      <Wrapper>
        <CalendarWrapper>
          <CalendarYearMonthSelect
            changeYearMonth={this.changeYearMonth}
            yearMonth={this.state.yearMonth}
          />
          <CalendarGrid
            handleToggleClose={this.handleToggleClose}
            handleUpdateDeparture={this.handleUpdateDeparture}
            fetching={this.state.fetching}
            data={this.state.data}
            isBokunData={!!this.props.post.bokun_activity_id}
            isAgentPrivateTour={
              this.props.post.post_type === 'private' && this.props.agent === true
            }
          />
        </CalendarWrapper>
      </Wrapper>
    )
  }
}

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  color: ${constants.COLORS.black};
  letter-spacing: 0.5px;
  user-select: none;
`

const CalendarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: auto;
`

export default Calendar
